/**
 * Concierge domains: endings CortexGrip shows in search but registers only through its team. Instead
 * of "Select", the customer sends a request ("Contact us") and the team gets back to them; the
 * request's progress shows on Find a domain.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ConciergeBell, Mail, MessageCircle, Phone, Sparkles } from "lucide-react";
import { useId, useState, type FormEvent, type ReactNode } from "react";

import { useOrganization } from "@/auth/organization";
import { useUser } from "@/auth/session";
import {
  ErrorAlert,
  Form,
  cx,
  Modal,
  Field,
  Input,
  CONTROL_LG,
  Button,
  Section,
  useToast,
} from "@/components/ui";
import {
  type DomainRequest,
  type DomainRequestStatus,
  type SearchResult,
  domainKeys,
  domainsApi,
  longDate,
} from "@/lib/domains";
import { formatMoney } from "@/lib/format";

type Channel = DomainRequest["preferred_channel"];

const CHANNELS: { id: Channel; label: string; icon: ReactNode }[] = [
  { id: "email", label: "E-mail", icon: <Mail className="size-4" /> },
  { id: "phone", label: "Phone call", icon: <Phone className="size-4" /> },
  { id: "whatsapp", label: "WhatsApp", icon: <MessageCircle className="size-4" /> },
];

const STATUS: Record<DomainRequestStatus, { label: string; className: string }> = {
  new: { label: "Sent to our team", className: "bg-[#fef4e5] text-[#853f00]" },
  contacted: { label: "In conversation", className: "bg-[#e8f0fc] text-[#2457a8]" },
  fulfilled: { label: "Done", className: "bg-[#eef8f3] text-[#007955]" },
  declined: { label: "Not possible", className: "bg-[#f3ebe7] text-[#5c5551]" },
  cancelled: { label: "Cancelled", className: "bg-[#f3ebe7] text-[#5c5551]" },
};

export function defaultConciergeMessage(tld: string): string {
  return `.${tld} domains are registered by the CortexGrip team. Send us a request and we'll be in touch.`;
}

/** The gold "Concierge" pill that replaces "Available" on restricted endings. */
export function ConciergePill({ requested }: { requested?: boolean }) {
  return requested ? (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#e8f0fc] py-[3px] pr-[9px] pl-2 text-[11.5px] font-semibold whitespace-nowrap text-[#2457a8]">
      <Check className="size-3" strokeWidth={3} /> Requested
    </span>
  ) : (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-gradient-to-r from-[#fdf0d5] to-[#fbe3c4] py-[3px] pr-[9px] pl-2 text-[11.5px] font-semibold whitespace-nowrap text-[#7a4a00] ring-1 ring-[#f1d29b]">
      <Sparkles className="size-3" /> Concierge
    </span>
  );
}

export function useDomainRequests() {
  return useQuery({
    queryKey: domainKeys.requests(),
    queryFn: () => domainsApi.requests(),
    staleTime: 60_000,
  });
}

/** "Contact us" for one restricted name: who to reach, how, and anything the team should know. */
export function ConciergeModal({ result, onClose }: { result: SearchResult | null; onClose: () => void }) {
  const user = useUser();
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const [name, setName] = useState(user.display_name || "");
  const [email, setEmail] = useState(user.email);
  const [phone, setPhone] = useState("");
  const [channel, setChannel] = useState<Channel>("email");
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState<DomainRequest | null>(null);
  const formId = useId();

  const send = useMutation({
    mutationFn: () =>
      domainsApi.createRequest({
        fqdn: result!.fqdn,
        organization_id: orgId,
        contact_name: name.trim(),
        contact_email: email.trim(),
        contact_phone: phone.trim() || null,
        preferred_channel: channel,
        message: message.trim() || null,
        availability: result!.status,
        quoted_register_cents: result!.register_cents,
      }),
    onSuccess: async (req) => {
      setSent(req);
      await Promise.all([
        qc.invalidateQueries({ queryKey: domainKeys.requests() }),
        qc.invalidateQueries({ queryKey: ["domains", orgId, "search"] }),
      ]);
    },
  });

  if (!result) return null;
  const tld = result.fqdn.split(".").slice(1).join(".");
  const needsPhone = channel !== "email";
  const ready = name.trim() && email.includes("@") && (!needsPhone || phone.trim());

  function close() {
    setSent(null);
    send.reset();
    setMessage("");
    onClose();
  }

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (ready && !send.isPending) send.mutate();
  };

  if (sent) {
    const how =
      sent.preferred_channel === "email"
        ? `by e-mail at ${sent.contact_email}`
        : sent.preferred_channel === "whatsapp"
          ? `on WhatsApp at ${sent.contact_phone}`
          : `by phone on ${sent.contact_phone}`;
    return (
      <Modal open onClose={close} title="Request sent" width={460}>
        <div className="flex flex-col items-center gap-3 py-2 text-center">
          <span className="flex size-14 items-center justify-center rounded-full bg-[#eef8f3] text-[#007955]">
            <Check className="size-7" strokeWidth={2.5} />
          </span>
          <p className="text-lg font-semibold text-fg">{sent.display_name}</p>
          <p className="text-sm text-[#5c5551]">
            Our team has your request and will reach you {how}, usually within one business day.
          </p>
          <span className="rounded-lg bg-[#f3ebe7] px-3 py-1.5 font-mono text-xs text-[#5c5551]">
            Reference {sent.number}
          </span>
        </div>
        <Button variant="brand" size="lg" className="w-full" onClick={close}>
          Done
        </Button>
      </Modal>
    );
  }

  return (
    <Modal
      open
      onClose={close}
      width={520}
      icon={
        <span className="flex size-11 items-center justify-center rounded-[12px] bg-gradient-to-br from-[#2a2320] to-[#1a1513] text-[#f8d79a] shadow-[0_6px_12px_rgba(26,20,18,0.25)]">
          <ConciergeBell className="size-5" />
        </span>
      }
      title={`Let's get you ${result.display_name}`}
      subtitle="This ending is registered by our domain team, so you can't add it to the cart"
      note="No payment now. We confirm the price and any registry requirements first."
      footer={
        <>
          <Button variant="secondary" size="lg" onClick={close}>
            Cancel
          </Button>
          <Button variant="brand" size="lg" type="submit" form={formId} loading={send.isPending}>
            Send request
          </Button>
        </>
      }
    >
      {/* the name, as a ticket */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#221c19] to-[#3a2a22] p-4 text-[#fef8f6]">
        <span className="pointer-events-none absolute -top-10 -right-8 size-32 rounded-full bg-[#f24e29]/25 blur-2xl" />
        <p className="text-[11px] font-semibold tracking-[0.08em] text-[#f8d79a] uppercase">
          Concierge registration
        </p>
        <p className="mt-1 truncate text-xl font-semibold">{result.display_name}</p>
        <p className="mt-1 text-xs text-[#d4cac4]">
          {result.restriction_message ?? defaultConciergeMessage(tld)}
        </p>
        {result.register_cents !== null && (
          <p className="mt-3 inline-flex rounded-full bg-white/10 px-2.5 py-1 text-[11.5px] text-[#fef8f6]">
            Guide price {formatMoney(result.register_cents, "GBP")} first year + VAT — we'll confirm it
          </p>
        )}
      </div>

      <ol className="grid grid-cols-3 gap-2 text-[11.5px] text-[#5c5551]">
        {["You send this request", "We confirm price & eligibility", "We register it for your workspace"].map(
          (step, i) => (
            <li key={step} className="flex flex-col gap-1.5 rounded-xl bg-[#faf6ef] p-2.5">
              <span
                className={cx(
                  "flex size-5 items-center justify-center rounded-full text-[11px] font-bold",
                  i === 0 ? "bg-brand-500 text-white" : "bg-white text-[#5c5551]",
                )}
              >
                {i + 1}
              </span>
              {step}
            </li>
          ),
        )}
      </ol>

      <Form id={formId} onSubmit={submit} error={send.error} className="flex flex-col gap-4">
        <div className="flex flex-col gap-4 sm:flex-row">
          <Field size="lg" label="Your name" name="contact_name" required>
            {(control) => (
              <Input
                size="lg"
                {...control}
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={120}
                autoComplete="name"
              />
            )}
          </Field>
          <Field size="lg" label="E-mail" name="contact_email" required>
            {(control) => (
              <Input
                size="lg"
                {...control}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                maxLength={320}
                autoComplete="email"
              />
            )}
          </Field>
        </div>

        <fieldset className="flex flex-col gap-1.5">
          <legend className="mb-1.5 text-[13px] font-semibold text-[#5c5551]">
            How should we reach you?
          </legend>
          <div className="grid grid-cols-3 gap-2">
            {CHANNELS.map((c) => (
              <label
                key={c.id}
                className={cx(
                  "flex cursor-pointer items-center justify-center gap-1.5 rounded-[10px] border px-2 py-2.5 text-[13px] font-semibold transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-brand-500/30",
                  channel === c.id
                    ? "border-brand-500 bg-[#fef8f6] text-brand-600"
                    : "border-border bg-surface text-[#5c5551] hover:bg-muted",
                )}
              >
                <input
                  type="radio"
                  name="channel"
                  value={c.id}
                  checked={channel === c.id}
                  onChange={() => setChannel(c.id)}
                  className="sr-only"
                />
                {c.icon}
                {c.label}
              </label>
            ))}
          </div>
        </fieldset>

        <Field
          label="Phone number"
          name="contact_phone"
          required={needsPhone}
          optional={!needsPhone}
          hint={needsPhone ? "With country code, e.g. +44 7700 900123" : undefined}
        >
          {(control) => (
            <Input
              size="lg"
              {...control}
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              maxLength={40}
              autoComplete="tel"
              placeholder="+44 7700 900123"
            />
          )}
        </Field>

        <Field size="lg" label="Anything we should know?" name="message" optional>
          {(control) => (
            <textarea
              {...control}
              rows={3}
              value={message}
              maxLength={1000}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Budget, timing, or other names you'd consider…"
              className={cx(CONTROL_LG, "resize-y")}
            />
          )}
        </Field>
        {/* Hidden Enter-key submit trigger, not a visible action — not the shared Button. */}
        {/* eslint-disable-next-line portal/no-raw-button */}
        <button type="submit" hidden />
        <ErrorAlert error={send.error} />
      </Form>
    </Modal>
  );
}

/** "Your domain requests": what the team is working on for this person. */
export function DomainRequestsPanel() {
  const qc = useQueryClient();
  const requests = useDomainRequests();
  const toast = useToast();
  const cancel = useMutation({
    mutationFn: (id: string) => domainsApi.cancelRequest(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: domainKeys.requests() });
      toast.show({ tone: "success", title: "Domain request withdrawn" });
    },
  });
  const shown = (requests.data?.requests ?? []).filter((r) => r.status !== "cancelled").slice(0, 6);
  if (shown.length === 0) return null;
  return (
    <Section title="Your domain requests" subtitle="Names our team is getting for you">
      <ul className="flex flex-col">
        {shown.map((r, i) => {
          const s = STATUS[r.status];
          return (
            <li key={r.id} className={cx("flex flex-col gap-1.5 py-3", i > 0 && "border-t border-border")}>
              <div className="flex items-center gap-2">
                <ConciergeBell className="size-4 shrink-0 text-[#b07a1f]" />
                <p className="min-w-0 flex-1 truncate text-sm font-semibold text-fg">{r.display_name}</p>
                <span
                  className={cx("shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold", s.className)}
                >
                  {s.label}
                </span>
              </div>
              <div className="flex items-center gap-2 pl-6 text-xs text-fg-subtle">
                <span className="font-mono">{r.number}</span>
                <span>·</span>
                <span>{longDate(r.created_at)}</span>
                {(r.status === "new" || r.status === "contacted") && (
                  <Button
                    unstyled
                    type="button"
                    onClick={() => cancel.mutate(r.id)}
                    disabled={cancel.isPending}
                    aria-busy={cancel.isPending}
                    className="ml-auto font-semibold text-fg-subtle hover:text-fg disabled:opacity-50"
                  >
                    Withdraw
                  </Button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      <ErrorAlert error={cancel.error} />
    </Section>
  );
}
